import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from '../commonComponents/SecurityIdentityFieldSelector';
import { CountryFlagText } from '../shared/securityDisplayPrimitives';
import { securityIdentityPresentation } from '../../utils/securityIdentityPresentation';
import DataTable from '../data_table/DataTable';
import Tooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';

const money = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

const STATUS = Object.freeze({
    included: {
        Icon: CheckCircle2,
        label: 'Included in Watchlist',
        className: 'text-ui-success',
    },
    missing: {
        Icon: AlertCircle,
        label: 'Missing from Watchlist',
        className: 'text-ui-warning',
    },
});

const getSecurityRowId = (row) => row.cognive_id;

// Only the Country definition retained in this saved receipt can label its
// identities. A custom Country column or a current ticker is not equivalent.
function savedCountries(attributes) {
    const countries = new Map();
    if (attributes?.attribute_contract_version !== 3) return countries;
    const definitions = (Array.isArray(attributes.definitions) ? attributes.definitions : []).filter((definition) => (
        definition?.source_kind === 'system_derived' && definition.system_key === 'country'
    ));
    if (definitions.length !== 1 || typeof definitions[0].attribute_definition_id !== 'string'
        || !definitions[0].attribute_definition_id) return countries;
    for (const value of Array.isArray(attributes.values) ? attributes.values : []) {
        if (value?.attribute_definition_id !== definitions[0].attribute_definition_id) continue;
        const country = !countries.has(value.cognive_id) && typeof value.value_text === 'string'
            && /^[A-Z]{3}$/.test(value.value_text)
            ? value.value_text : null;
        countries.set(value.cognive_id, country);
    }
    return countries;
}

const NO_SECURITIES = Object.freeze([]);

/**
 * Every security of a saved receipt, in receipt order, in one scrolling
 * list: identity (the field picked in its header), the saved Country, the
 * saved GMV and whether the security is still in the Watchlist. Without a
 * `detail` (still loading) the table shows its placeholder rows.
 */
export default function OptimizerSavedSecuritiesTable({ detail = null, savedAum }) {
    const [identityField, setIdentityField] = useState('company_name');
    const countries = useMemo(() => savedCountries(detail?.watchlist_attributes), [detail]);
    const presentations = useMemo(() => new Map(
        (detail?.security_presentations || []).map((row) => [row.cognive_id, row]),
    ), [detail]);
    // The receipt contains the whole saved selection, including securities
    // removed from the Watchlist and positions pruned from the saved portfolio.
    const rows = detail ? detail.result.securities : NO_SECURITIES;
    const included = useMemo(
        () => new Set(detail?.compatibility.available_cognive_ids),
        [detail],
    );
    const columns = useMemo(() => [
        {
            id: 'identity',
            header: securityIdentityFieldLabel(identityField),
            enableSorting: false,
            minSize: 200,
            // Receipt result.ticker may contain a submitted SEDOL or other
            // identifier. Only the dedicated display projection labels rows.
            cell: ({ row: { original } }) => {
                const identity = securityIdentityPresentation(presentations.get(original.cognive_id), identityField);
                return (
                    <Tooltip content={identity.tooltip}>
                        <span className="block truncate">{identity.value}</span>
                    </Tooltip>
                );
            },
            meta: {
                grow: true,
                headerControls: () => (
                    <SecurityIdentityFieldMenu value={identityField} onChange={setIdentityField} />
                ),
            },
        },
        {
            id: 'country',
            header: 'Country',
            size: 140,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const country = countries.get(original.cognive_id);
                return country ? <CountryFlagText country={country} /> : 'Unavailable';
            },
        },
        {
            id: 'gmv',
            header: 'Saved GMV',
            size: 130,
            enableSorting: false,
            cell: ({ row: { original } }) => money.format(original.gmv_optimized),
            meta: { align: 'right' },
        },
        {
            id: 'status',
            header: 'In Watchlist',
            size: 116,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const status = included.has(original.cognive_id) ? STATUS.included : STATUS.missing;
                return (
                    <Tooltip content={status.label}>
                        <status.Icon
                            role="img"
                            aria-label={status.label}
                            aria-hidden={false}
                            size={18}
                            strokeWidth={2.25}
                            className={cx('inline-block align-middle', status.className)}
                        />
                    </Tooltip>
                );
            },
            meta: { align: 'center', wrap: true },
        },
    ], [countries, identityField, included, presentations]);
    return <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
            <Typography variant="subtitle2">Saved securities{detail && ` (${rows.length})`}</Typography>
            {Number.isFinite(savedAum) && savedAum > 0 && (
                <Typography variant="caption" color="text.secondary">Saved AUM: {money.format(savedAum)}</Typography>
            )}
        </Box>
        <DataTable
            aria-label="Saved portfolio securities"
            data={rows}
            columns={columns}
            loading={!detail}
            getRowId={getSecurityRowId}
            maxHeight={280}
            rowHeight={32}
            headerHeight={34}
            virtualize
        />
    </Box>;
}
